import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_SUBTAB, useCommandCenterSubtab } from '../../../../../../mini-apps/command-center/subtabs';
import {
  COMMAND_CENTER_BASE,
  accountInCommandCenter,
  contactInCommandCenter,
} from '../../../../../../mini-apps/_shared/flow-links';
import Header from './Header';
import type { FlowRole } from '@shared/flow/role';
import { useRoleView } from '../../../hooks/useRoleView';
import type { HeaderVariant } from './shell-chrome';
import { hiddenSubtabsForTeam } from './role-subtabs';

/** Search-result navigation shared by both headers (FlowShell's old handlers, verbatim). */
function useSearchSelectHandlers() {
  const navigate = useNavigate();
  const onSelectAccount = (accountId: string) => navigate(accountInCommandCenter(accountId));
  const onSelectContact = (contactId: string, accountId: string | null) => {
    if (accountId) {
      navigate(contactInCommandCenter(accountId, contactId));
    } else {
      // Orphan contact (no account in the mirror) — land on the global
      // contacts list instead of crashing, same rule as the old shell.
      navigate(`${COMMAND_CENTER_BASE}?tab=contact`);
    }
  };
  return { onSelectAccount, onSelectContact };
}

/**
 * Flow's tabbed header. `useCommandCenterSubtab` lives ONLY here: it reads
 * `?tab=`/`?q=`, which collide with other routes' own params (LibraryPage's
 * `?tab=`), so it must never run off `/flow/*`.
 */
function FlowHeader() {
  const { subtab, onHome, setSubtab, initialQuery } = useCommandCenterSubtab(COMMAND_CENTER_BASE);
  const handlers = useSearchSelectHandlers();
  // Role-gated like the rail's items: Signals renders only once the role has
  // loaded, so a GS rep never sees it flash in and out.
  const { team, isLoaded } = useRoleView();
  const hiddenTabs = hiddenSubtabsForTeam(team, isLoaded);
  // A live role switch (impersonation) away from a role that shows the tab
  // moves a rep off the now-hidden Signals tab. First load and direct
  // `?tab=signals` links are left alone.
  const prevTeam = useRef<FlowRole | null | undefined>(undefined);
  useEffect(() => {
    if (!isLoaded) return;
    const prev = prevTeam.current;
    prevTeam.current = team;
    if (prev === undefined || prev === team) return;
    if (onHome && hiddenTabs.includes(subtab)) setSubtab(DEFAULT_SUBTAB);
  }, [isLoaded, team, onHome, subtab, hiddenTabs, setSubtab]);
  return <Header subtab={subtab} setSubtab={setSubtab} hiddenTabs={hiddenTabs} initialQuery={initialQuery} {...handlers} />;
}

/** Search-only header for every non-Flow route: no tabs, no URL-param reads. */
function PlainHeader() {
  const handlers = useSearchSelectHandlers();
  return <Header {...handlers} />;
}

/** The 48px header: Flow's tabbed header or the plain one, as `resolveChrome` decided. */
export default function ShellHeader({ variant }: { variant: HeaderVariant }) {
  return variant === 'flow-tabs' ? <FlowHeader /> : <PlainHeader />;
}
